<style lang="scss" scoped>
h4 {
    font-size: var(--font-size-large);
    line-height: 1.5;
    margin-top: 0;
    margin-bottom: 0;
    font-weight: bold;

    @media (width <= 390px) {
        font-size: 18px;
    }
}

.content-info {
    max-width: 450px;
    line-height: 2;
}

.button-bar {
    margin-bottom: 5px;

    .button.button-high.button-primary,
    .button.button-high.button-alternate {
        padding: 16px 10px;
        min-width: 250px;
        width: auto;
    }
}

.shop-link {
    font-style: italic;
    font-weight: 600;
    color: var(--color-main-font);
    transition: color 0.3s;

    &:hover,
    &:focus {
        color: var(--color-alternate);
    }
}

.log-in-text {
    text-align: center;
    font-size: 13px;
    line-height: 1.4;
    display: block;
    margin-top: 30px;
}
</style>

<template>
    <div v-if="downloadLimitExceeded && downloadLimitExceeded.type === 'FAIR_POLICY'">
        <h3>{{ t.popup.fair_condition }}</h3>
        <h4>{{ t.popup.fair_condition_1 }}</h4>
        <div class="content-info">
            <p>
                {{ t.popup.fair_condition_2 }}
                <span v-if="downloadLimitExceeded.maxValue != 1">
                    {{ downloadLimitExceeded.maxValue }}
                    {{
                        downloadLimitExceeded.contentType === "PACKAGE"
                            ? t.content.total_packages
                            : t.content.total_contents
                    }}
                </span>
                <span v-else>
                    {{ t.popup.fair_condition_3 }}
                    {{ downloadLimitExceeded.contentType === "PACKAGE" ? t.content.package : t.content.single_content }}
                </span>
                {{ t.popup.fair_condition_4 }}
                <span v-if="userLicenceName === 'Premium'">{{ t.popup.fair_condition_5 }}</span>
                <span v-else>{{ t.popup.fair_condition_6 }}</span> {{ t.popup.fair_condition_7 }}
            </p>
        </div>
        <div class="button-bar">
            <BaseButton type="high" @click="$emit('close')">{{ t.buttons.to_tutkit }}</BaseButton>
        </div>
    </div>

    <div v-else-if="downloadLimitExceeded && downloadLimitExceeded.type === 'LICENSE'">
        <h3 v-if="userLicenceName !== 'Tester'">{{ t.popup.limit_reached }}</h3>
        <h3 v-else>{{ t.popup.download_popup_title }}</h3>
        <h4 v-if="userLicenceName !== 'Tester'">{{ t.popup.upgrade_message }}</h4>
        <h4 v-else>{{ t.popup.limit_reached }}</h4>
        <div class="content-info">
            <p v-if="!convertToBoolean(testLicenceCanceled) && !isLicenseTester">
                {{ t.popup.limit_reached_message_1 }}
                <span v-if="downloadLimitExceeded.maxValue != 1">
                    {{
                        downloadLimitExceeded.contentType === "PACKAGE"
                            ? t.content.total_packages
                            : t.content.total_contents
                    }}
                </span>
                <span v-else>
                    {{ downloadLimitExceeded.contentType === "PACKAGE" ? t.content.package : t.content.single_content }}
                </span>
                {{ t.popup.limit_reached_message_2 }} ({{ downloadLimitExceeded.maxValue }}/{{
                    downloadLimitExceeded.maxValue
                }}).
                {{ t.popup.limit_reached_message_3 }}
            </p>
            <p v-else>
                {{ t.popup.limit_reached_message_4 }}
            </p>
        </div>
        <div class="button-bar">
            <BaseButton type="high" target="_direct" :href="routePriceModel">{{ t.buttons.upgrade_2 }}</BaseButton>
        </div>
    </div>

    <div v-else-if="!isInCart && !singleOrdered && (isLicenseActive || isLicenseTester || !hasUser)">
        <h3>{{ t.popup.flatrate_package }}</h3>
        <h4>{{ t.popup.choose_option }}</h4>
        <div class="content-info">
            <p>
                {{ t.popup.choose_option_message }}
            </p>
        </div>
        <div class="button-bar">
            <BaseButton v-if="showButtons?.green" type="high" target="_direct" :href="showButtons.green.url">
                {{ showButtons.green.text_button }}
            </BaseButton>
            <BaseButton
                v-if="showButtons?.blue"
                type="high"
                target="_direct"
                :href="showButtons.blue.url"
                @click.prevent="
                    showButtons.blue.data_package
                        ? addToCart(showButtons.blue.data_package)
                        : goToHref(showButtons.blue.url)
                "
                color="alternate"
                >{{ showButtons.blue.text_button }}
            </BaseButton>
        </div>
    </div>
    <div v-else-if="isInCart">
        <h3>{{ t.popup.packages_already_in_cart }}</h3>
        <h4>{{ t.popup.continue_order }}</h4>
        <div class="content-info">
            <p>
                {{ t.popup.continue_order_message }}
            </p>
        </div>
        <div class="button-bar single-info">
            <BaseButton
                v-if="showButtons?.blue"
                color="alternate"
                type="high"
                target="_direct"
                :href="showButtons.blue.url"
            >
                <span class="button-text">
                    {{ showButtons.blue.text_button }}
                </span>
                <span class="button-small-text">
                    {{ showButtons.blue?.text_button_small }}
                </span>
            </BaseButton>
        </div>
    </div>
    <div v-else-if="singleOrdered && singleNotPaid">
        <h3>{{ t.popup.package_already_ordered }}</h3>
        <h4>{{ t.popup.complete_your_order }}</h4>
        <div class="content-info">
            <p>
                {{ t.popup.complete_your_order_message }}
            </p>
        </div>
        <div class="button-bar single-info">
            <BaseButton
                v-if="showButtons?.blue"
                color="alternate"
                type="high"
                target="_direct"
                :href="showButtons.blue.url"
            >
                <span class="button-text">
                    {{ showButtons.blue.text_button }}
                </span>
                <span class="button-small-text">
                    {{ showButtons.blue?.text_button_small }}
                </span>
            </BaseButton>
        </div>
    </div>
    <div v-else-if="downloadNotPossible && hasUser">
        <h3>{{ subTitle }}</h3>
        <h4>{{ title }}</h4>
        <p>{{ t.popup.account_inactive }}</p>
        <div class="button-bar">
            <BaseButton type="high" color="alternate" target="_blank" :href="routeContact"
                >{{ t.buttons.contact_us }}
            </BaseButton>
            <BaseButton type="high" target="_blank" :href="routeUpgrade">{{ t.buttons.account_settings }}</BaseButton>
        </div>
    </div>
</template>

<script>
import axios from "@/app/axios.ts"
import BaseButton from "@/components/Buttons/BaseButton.vue"
import { openLoginRegistrationPopup } from "@/libs/helpers.ts"
const translationsData = window.translationsData

export default {
    components: { BaseButton },
    props: [
        "contentID",
        "title",
        "hasUser",
        "downloadLimitExceeded",
        "downloadNotPossible",
        "contentType",
        "status",
        "testLicenceCanceled",
        "userLicenceName",
        "isInCart",
        "singleOrdered",
        "singleNotPaid",
        "isLicenseActive",
        "isLicenseTester",
        "buttons",
    ],
    data() {
        return {
            showButtons: [],
            globalRoute: window.translationsData.global_route,
            t: translationsData.translations,
        }
    },
    methods: {
        openLoginPopup() {
            this.$emit("close")
            openLoginRegistrationPopup()
        },
        routeLogin() {
            let compatibilityPopup = document.getElementById("compatibility-popup-container")
            if (compatibilityPopup) {
                compatibilityPopup.innerHTML = "<div></div>"
            }

            this.$emit("close")
            openLoginRegistrationPopup()
        },
        routeRegister() {
            let compatibilityPopup = document.getElementById("compatibility-popup-container")
            if (compatibilityPopup) {
                compatibilityPopup.innerHTML = "<div></div>"
            }

            this.$emit("close")
            openLoginRegistrationPopup("registration")
        },
        psdShopLink() {
            return "https://shop.psd-tutorials.de/produktdetails/produkt--" + this.contentID + "?empfohlen_von=tutkit"
        },

        async sendStats() {
            this.$emit("close", false)
        },

        goToHref(href) {
            window.location.href = href
        },
        async addToCart(packageId) {
            if (packageId) {
                try {
                    const response = await axios.post(this.globalRoute.cart + "/add", {
                        package_id: packageId,
                    })
                    if (response) {
                        window.location.href = this.globalRoute.cart
                    }
                } catch (e) {
                    this.$error(e)
                }
            }
        },
        convertToBoolean(value) {
            if (typeof value === "string") {
                return value.toLowerCase() === "true"
            }

            return !!value
        },
    },
    computed: {
        routeUpgrade() {
            return this.globalRoute.upgrade
        },
        routeContact() {
            return this.globalRoute.contact
        },
        routePriceModel() {
            return this.globalRoute.price_model
        },
        notPossibleText() {
            if (this.contentType === "tutorial") {
                return "Um das Text-Tutorial lesen zu können, registriere dich oder logge dich ein."
            } else if (this.contentType === "video") {
                return "Noch hast du keinen Zugriff auf dieses Video."
            } else if (this.contentType === "pdf") {
                return "Noch hast du keinen Zugriff auf diese Pdf-Anleitung."
            } else if (this.contentType === "working-file") {
                return "Noch hast du keinen Zugriff auf diese Projekt-Datei."
            } else if (this.contentType === "ebook") {
                return "Noch hast du keinen Zugriff auf dieses E-Book."
            }
            return "Noch hast du keinen Zugriff auf den Download."
        },
        subTitle() {
            if (this.contentType === "tutorial") {
                return "Text-Tutorial"
            } else if (this.contentType === "video") {
                return "Tutorial"
            } else if (this.contentType === "pdf") {
                return "Tutorial"
            } else if (this.contentType === "working-file") {
                return "Projektdateien"
            } else if (this.contentType === "ebook") {
                return "Tutorial"
            }
            return "Download"
        },
    },

    mounted() {
        this.showButtons = JSON.parse(this.buttons)
    },
}
</script>
